import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9aoluajutvtlj0vtb4g';

export default function ContactPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setMessageLength(0);
      return;
    }

    formData.delete('website_alt');
    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || t('errors.formSubmitError'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('errors.formSubmitError'));
    }
  };

  const infoCards = [
    { icon: 'ri-phone-line', label: t('contactPage.callLabel'), value: '(657) 433-8533', href: 'tel:+16574338533' },
    { icon: 'ri-mail-line', label: t('contactPage.emailLabel'), value: 'thescreensquadcrew@gmail.com', href: 'mailto:thescreensquadcrew@gmail.com' },
    { icon: 'ri-arrow-right-up-line', label: t('contactPage.socialLabel'), value: 'The Screen Squad', href: '#' },
    { icon: 'ri-map-pin-line', label: t('contactPage.areaLabel'), value: 'Santa Ana, CA', subValue: t('contactPage.areaSub'), href: '#' },
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative w-full min-h-[360px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="The Screen Squad contact"
              className="w-full h-full object-cover object-center"
              src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/b68a5f5d-06c5-42c7-9e0d-679e4bf24804_compressed_magnific_reframe-this-exact-photog_1sq9KGxr4r.webp"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>
          <div className="relative z-10 text-center mt-12">
            <span className="inline-block bg-black/80 border-2 border-primary-500 text-white text-2xl md:text-4xl font-extrabold tracking-[0.15em] uppercase px-20 py-6 rounded-lg backdrop-blur-sm">
              {t('contactPage.heroTitle')}
            </span>
          </div>
        </section>

        {/* Info Cards - FMO Style */}
        <div className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 px-4 md:px-6">
            {infoCards.map((card, i) => (
              <a
                key={i}
                href={card.href}
                className={`flex items-center gap-4 px-6 py-8 hover:bg-white/5 transition-colors cursor-pointer ${
                  i !== infoCards.length - 1 ? 'lg:border-r lg:border-white/10' : ''
                }`}
              >
                <i className={`${card.icon} text-primary-500 text-xl w-6 h-6 flex items-center justify-center shrink-0`} />
                <div>
                  <span className="text-primary-500 text-xs font-bold uppercase tracking-[0.2em] block">{card.label}</span>
                  <span className="text-foreground-50 text-sm font-semibold mt-0.5 block">{card.value}</span>
                  {card.subValue && <span className="text-foreground-400 text-xs block">{card.subValue}</span>}
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Contact Form + Info with Background Image */}
        <section className="relative py-20 overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              alt="Background"
              className="w-full h-full object-cover object-center"
              src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/0856088a-a6f0-4103-8dcb-63696ea332f1_compressed_WhatsApp-Image-2026-07-13-at-11.23.23-PM.webp"
            />
            <div className="absolute inset-0 bg-black/80" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Left - Info */}
              <div className="w-full lg:w-[380px] shrink-0">
                <div className="bg-background-400/60 border border-primary-500/25 rounded-2xl p-6 flex flex-col">
                  <img
                    alt="The Screen Squad Logo"
                    className="h-16 w-auto object-contain mb-4"
                    src="https://static.readdy.ai/image/e2264d4847cbc98dfbe6fd1d0e6b7ca4/c480cc975a256a5055b1791bd11fefde.png"
                  />
                  <h3 className="text-foreground-50 font-bold text-base mb-2">{t('contactPage.infoTitle')}</h3>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-6">{t('contactPage.infoDesc')}</p>

                  <div className="flex flex-col gap-4 flex-1">
                    <div className="bg-background-100 rounded-xl p-4 border border-white/5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-map-pin-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                        <span className="text-foreground-50 font-semibold text-xs uppercase tracking-wider">{t('contactPage.infoArea')}</span>
                      </div>
                      <p className="text-foreground-400 text-sm whitespace-pre-line">{t('contactPage.infoAreaValue')}</p>
                    </div>

                    <div className="bg-background-100 rounded-xl p-4 border border-white/5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-time-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                        <span className="text-foreground-50 font-semibold text-xs uppercase tracking-wider">{t('contactPage.infoHours')}</span>
                      </div>
                      <p className="text-foreground-400 text-sm">{t('contactPage.infoHoursWeekday')}</p>
                      <p className="text-foreground-400 text-sm">{t('contactPage.infoHoursWeekend')}</p>
                    </div>

                    <div className="bg-background-100 rounded-xl p-4 border border-white/5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-shield-check-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                        <span className="text-foreground-50 font-semibold text-xs uppercase tracking-wider">{t('contactPage.infoWhy')}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {[1, 2, 3, 4].map((n) => (
                          <li key={n} className="flex items-start gap-2">
                            <i className="ri-check-line text-primary-500 text-xs mt-0.5" />
                            <span className="text-foreground-400 text-xs">{t(`contactPage.infoWhy${n}` as const)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <p className="text-foreground-400/60 text-xs mt-4">{t('contactPage.infoNote')}</p>
                </div>
              </div>

              {/* Right - Form */}
              <div className="flex-1">
                <div className="bg-background-400/60 border border-primary-500/25 rounded-2xl p-8">
                  <div className="mb-6">
                    <span className="text-primary-500 text-xs font-bold uppercase tracking-[0.25em]">{t('contactPage.formTitle')}</span>
                    <h2 className="text-foreground-50 text-xl font-extrabold mt-2">{t('contactPage.formSubtitle')}</h2>
                  </div>

                  {status === 'success' ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 flex items-center justify-center bg-primary-500/15 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-primary-500 text-3xl" />
                      </div>
                      <p className="text-foreground-50 font-semibold text-lg">{t('success.formSubmit')}</p>
                    </div>
                  ) : (
                    <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                      <input
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                        name="website_alt"
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.name')} *</label>
                          <input
                            placeholder={t('contactPage.namePlaceholder')}
                            className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                            required
                            name="firstName"
                          />
                        </div>
                        <div>
                          <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.lastName')} *</label>
                          <input
                            placeholder={t('contactPage.lastNamePlaceholder')}
                            className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                            required
                            name="lastName"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.email')} *</label>
                        <input
                          placeholder={t('contactPage.emailPlaceholder')}
                          className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                          required
                          type="email"
                          name="email"
                        />
                      </div>

                      <div>
                        <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.phone')} *</label>
                        <input
                          placeholder={t('contactPage.phonePlaceholder')}
                          className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                          required
                          type="tel"
                          name="phone"
                        />
                      </div>

                      <div>
                        <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.address')}</label>
                        <input
                          placeholder={t('contactPage.addressPlaceholder')}
                          className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all"
                          name="propertyAddress"
                        />
                      </div>

                      <div>
                        <label className="block text-foreground-50 text-sm font-semibold mb-1.5">{t('contactPage.service')} *</label>
                        <select
                          name="service"
                          className="w-full bg-white/10 border border-foreground-50/20 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all cursor-pointer"
                          required
                          defaultValue=""
                        >
                          <option value="" disabled>{t('contactPage.servicePlaceholder')}</option>
                          <option value="Window Screens">{t('hero.form.services.sprayFoam')}</option>
                          <option value="Screen Repair">{t('hero.form.services.batt')}</option>
                          <option value="Door Screens">{t('hero.form.services.roll')}</option>
                          <option value="Sliding Door Repair">{t('hero.form.services.cellulose')}</option>
                          <option value="New Screen Installation">{t('hero.form.services.blownIn')}</option>
                          <option value="Pet Door Screens">{t('hero.form.services.attic')}</option>
                          <option value="Security Screens">{t('hero.form.services.wall')}</option>
                          <option value="On-Site Service">{t('hero.form.services.crawlSpace')}</option>
                          <option value="Screen Replacement">{t('hero.form.services.metalBuilding')}</option>
                          <option value="General Consultation">{t('hero.form.services.general')}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-foreground-50 text-sm font-semibold mb-1.5">
                          {t('contactPage.message')} <span className="text-foreground-400 text-xs">({messageLength}/500)</span>
                        </label>
                        <textarea
                          placeholder={t('contactPage.messagePlaceholder')}
                          maxLength={500}
                          rows={4}
                          name="message"
                          onChange={(e) => setMessageLength(e.target.value.length)}
                          className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 focus:bg-white/10 transition-all resize-none"
                        />
                      </div>

                      {status === 'error' && errorMsg && (
                        <p className="text-red-400 text-sm">{errorMsg}</p>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold py-4 rounded-lg text-sm transition-all duration-200 cursor-pointer"
                      >
                        {status === 'submitting' ? '...' : t('contactPage.submit')}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map + Areas */}
        <section className="w-full py-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-foreground-50 text-2xl md:text-3xl font-extrabold mb-2">
                {t('contactPage.areasTitle')}
              </h2>
              <p className="text-foreground-400 text-sm md:text-base max-w-xl mx-auto">
                {t('contactPage.areasDesc')}
              </p>
            </div>

            {/* Areas list - grid with checkmarks */}
            <div className="bg-[#0a2a5c] rounded-xl px-6 py-5 mb-6">
              <h3 className="text-accent-500 text-xs font-bold uppercase tracking-wider mb-4 text-center">
                {t('contactPage.areasSubtitle')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2.5">
                {[
                  'Santa Ana', 'Anaheim', 'Huntington Beach',
                  'Tustin', 'Irvine', 'Newport Beach',
                  'Orange', 'Costa Mesa', 'Garden Grove',
                  'Fountain Valley', 'Westminster', 'Yorba Linda',
                  'La Habra', 'Buena Park', 'Laguna Beach',
                  'Seal Beach', 'Fullerton', 'Brea',
                  'Placentia', 'Villa Park', 'Stanton',
                  'Los Alamitos', 'Midway City', 'Y Más',
                ].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-checkbox-circle-fill text-accent-500 text-sm" />
                    </span>
                    <span className="text-white text-sm font-medium whitespace-nowrap">{area}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <iframe
                title={t('contactPage.mapTitle')}
                src="https://maps.google.com/maps?q=Santa+Ana,CA&z=13&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="400"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="border-0"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}